import React, { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import ApplicationLayout from "../../layouts/ApplicationLayout"
import Select from "../../elements/Select"
import ReactSVG from "react-svg"
import ReportDownload from "../../assets/images/report_download.svg"
import ReportEdit from "../../assets/images/report_edit.svg"
import ReportCopy from "../../assets/images/report_copy.svg"
import UpArrow from "../../assets/images/up_arrow.svg"
import MUIDataTable from "mui-datatables"
import store from "../../actions/configureStore"
import { connect } from "react-redux"
import { Icon, Pagination } from "semantic-ui-react"
import { transactionReportColumnDefs, convertDateToString } from "../../constants/reports"
import ReportSideModal from "./ReportSideModal"
import MiniLoader from "../../components/shared/MiniLoader"
import FMCOrderHeader from "../../components/shared/FMCOrderHeader"
import ReportHistoryModal from "./ReportHistoryModal"
import SaveReportModal from "../../components/shared/SaveReportModal"

const FMCReporting = ({
  transactionDataSource,
  totalPages,
  totalEntries,
  reportSummaryData,
  reportFilters,
  loadingTransactionRecords,
  loadingFilterRecords
}) => {
  const [reportingType, setReportingType] = useState("transactions")
  const [filters, setFilters] = useState({})
  const [page, setPage] = useState(1)
  const [modalOpen, setModalOpen] = useState(false)
  const [openConfigureModal, setOpenConfigureModal] = useState(false)
  const [openSavedHistoryModal, setOpenSavedHistoryModal] = useState(false)
  const [filter, setFilter] = useState({})

  const { t } = useTranslation("reports")

  useEffect(() => {
    fetchData(page, filters)
    fetchFilters()
  }, [page])

  const handleOpen = () => {
    setModalOpen(true)
  }

  const handleClose = () => {
    setModalOpen(false)
  }

  const openConfigureSideModal = () => {
    setOpenConfigureModal(true)
    setFilter(null)
    if (filter) {
      fetchData(page, {})
      setFilters({})
    }
  }

  const fetchData = async (query, filters, filter = null) => {
    setFilter(filter)
    setFilters(filters)
    await store.dispatch({
      type: "LOAD_TRANSACTIONS_REPORT",
      payload: {
        page: query,
        filters: convertDateToString(filters),
        filter_key: filter && filter.filter_key
      },
      callback: afterLoadReportData(query, filters)
    })
    setOpenSavedHistoryModal(false)
  }

  const afterLoadReportData = (query, filters) => {
    fetchReportSummaryData(query, filters)
  }

  const fetchFilters = () => {
    store.dispatch({
      type: "LOAD_FILTERS_DATA"
    })
  }

  const fetchReportSummaryData = async (query, filters) => {
    store.dispatch({
      type: "LOAD_REPORT_SUMMARY_DATA",
      payload: { page: query, filters: convertDateToString(filters) }
    })
  }

  const downloadReports = () => {
    const downloadFilters = filter ? filter.filters : filters

    store.dispatch({
      type: "DOWNLOAD_TRANSACTIONS_REPORT",
      payload: {
        filters: convertDateToString(downloadFilters),
        filter_key: filter && filter.filter_key
      }
    })
  }

  const options = [{ key: "1", text: t("transactionReportOptionsLabel"), value: "transactions" }]

  const filterOptions = {
    selectableRows: "none",
    print: false,
    download: false,
    search: false,
    filter: false,
    viewColumns: false,
    fullWidth: true,
    page: page,
    serverSide: true,
    count: 1,
    rowsPerPage: 20,
    pagination: true,
    customFooter: () => {
      return (
        <div className="custom-pagination">
          <Pagination
            defaultActivePage={1}
            totalPages={totalPages}
            activePage={page}
            firstItem={null}
            lastItem={null}
            prevItem={{ content: t("previousLabel"), icon: "arrow left" }}
            nextItem={{
              content: (
                <span>
                  {" "}
                  {t("nextLabel")}{" "}
                  <Icon name="arrow right" style={{ float: "right", marginLeft: "0.357em" }} />{" "}
                </span>
              )
            }}
            onPageChange={onChange}
            siblingRange={1}
            ellipsisItem={
              totalPages > 3 ? { content: <Icon name="ellipsis horizontal" />, icon: true } : null
            }
          />
        </div>
      )
    },
    textLabels: {
      body: {
        noMatch: loadingTransactionRecords ? (
          <>
            <MiniLoader inverted={false} /> {t("loadingReportDataLabel")}
          </>
        ) : (
          <> {t("noMatchingData")} </>
        )
      }
    }
  }

  const onChange = (e, pageInfo) => {
    setPage(pageInfo.activePage)
  }

  const filterReportData = (filters, filter = null) => {
    setFilter(filter)
    setFilters(filters)
    fetchData(1, filters, filter)
    fetchReportSummaryData(1, filters)
    setOpenConfigureModal(false)
  }

  const clearFilters = () => {
    setFilters({})
    fetchData(1, {})
    fetchReportSummaryData(1, {})
  }

  const reportEdit = (filter) => {
    setFilters(filter.filters)
    setFilter(filter)
    setOpenSavedHistoryModal(false)
    setOpenConfigureModal(true)
  }

  const closeConfigureModal = (filter) => {
    setOpenConfigureModal(false)
    setFilter(filter)
  }

  const runReport = (filter) => {
    fetchData(1, filter.filters, filter)
    setFilters(filter.filters)
    if (openConfigureModal) {
      setOpenConfigureModal(false)
      setOpenConfigureModal(true)
    }
  }

  return (
    <React.Fragment>
      <ApplicationLayout>
        <FMCOrderHeader header={t("reports:reportsLabel")} />
        <span />
        <div className="reporting-section">
          <div className="reporting-header">
            <div className="reporting-header-left-section">
              <div className="reporting-type-section">
                <span className="reporting-type-label">{t("reports:reportsTypelabel")}</span>
                <div className="reporting-type-select-section">
                  <Select
                    onChange={(event) => event.target.value && setReportingType(event.target.value)}
                    options={options}
                    placeholder="Type: Transactions"
                    value={reportingType}
                  />
                </div>
                <div className="configure-new-data-button">
                  <div className="label-cover" onClick={() => openConfigureSideModal()}>
                    <span className="label">{t("reports:configureNewDataLabel")}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="reporting-header-right-section">
              <div className="history-header-section">
                <span className="saved-history-label">{t("reports:savedHistoryLabel")}</span>
                <div
                  className={`all-history-link-section hand-icon ${
                    !reportFilters.length > 0 && "disabled-icon"
                  }`}
                  onClick={() => reportFilters.length > 0 && setOpenSavedHistoryModal(true)}
                >
                  <span className="label ">{t("reports:allHistoryLabel")}</span>
                </div>
              </div>
              <div className="saved-reports-name-section">
                <div className="saved-reports-total-section">
                  <div className="saved-reports-total-cover">
                    <span className="saved-reports-label">{t("reports:savedReportsLabel")}</span>
                    <span className="saved-reports-count">{reportFilters.length}</span>
                  </div>
                </div>
                {loadingFilterRecords && <MiniLoader inverted={false} />}
                <div className="scrollable-container">
                  {reportFilters.map((reportFilter) => (
                    <span
                      className={`saved-reports-name hand-icon ${
                        filter &&
                        filter.filter_name === reportFilter.filter_name &&
                        "show-selected-report"
                      }`}
                      onClick={() => runReport(reportFilter)}
                    >
                      {reportFilter.filter_name}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="reporting-main-content">
            <div className="main-content">
              <MUIDataTable
                title={
                  <div className="reporting-main-content-card-header">
                    <div className="card-header-left-section">
                      <div className="summary-data-section">
                        <div className="summary-data-label-cover">
                          <span className="label">{t("reports:totalSpentLabel")}</span>
                        </div>
                        <span className="summary-data-amount">
                          {reportSummaryData ? (
                            `$${Number(reportSummaryData.totals || 0).toFixed(2)}`
                          ) : (
                            <MiniLoader inverted={false} />
                          )}
                        </span>
                      </div>
                      <div className="summary-data-section">
                        <div className="summary-data-label-cover">
                          <span className="label">{t("reports:totalSavingLabel")}</span>
                          {/* <div className="show-up-percentage-section">
                            <div className="show-up-percentage-cover">
                              <div className="up-arrow-icon">
                                <ReactSVG src={UpArrow} />
                              </div>
                              <span className="label">10.0%</span>
                            </div>
                          </div> */}
                        </div>
                        <span className="summary-data-amount">
                          {reportSummaryData ? (
                            `$${Number(reportSummaryData.savings || 0).toFixed(2)}`
                          ) : (
                            <MiniLoader inverted={false} />
                          )}
                        </span>
                      </div>
                    </div>
                    <div>
                      {filter && <div className="current-report-name">{filter.filter_name}</div>}
                      <div className="card-header-right-section">
                        <div className="clear-filter-link" onClick={() => clearFilters()}>
                          {t("reports:clearFiltersLabel")}
                        </div>
                        <span className="transaction-found-label">
                          {t("reports:transactionFoundLabel")}
                        </span>
                        <span className="transaction-total-number">{totalEntries}</span>
                        <div
                          className={`reports-icon hand-icon ${
                            transactionDataSource &&
                            transactionDataSource.length == 0 &&
                            "disabled-icon"
                          }`}
                          onClick={() =>
                            transactionDataSource &&
                            transactionDataSource.length > 0 &&
                            downloadReports()
                          }
                        >
                          <ReactSVG src={ReportDownload} />
                        </div>
                        <div className={`reports-icon hand-icon ${!filter && "disabled-icon"}`}>
                          <ReactSVG src={ReportEdit} onClick={() => filter && reportEdit(filter)} />
                        </div>
                        <div
                          className={`reports-icon hand-icon ${
                            !(filters && Object.keys(filters).length > 0 && !filter) &&
                            "disabled-icon"
                          }`}
                          onClick={() =>
                            filters &&
                            Object.keys(filters).length > 0 &&
                            !filter &&
                            setModalOpen(true)
                          }
                        >
                          <ReactSVG src={ReportCopy} />
                        </div>
                      </div>
                    </div>
                  </div>
                }
                data={transactionDataSource}
                columns={transactionReportColumnDefs()}
                options={filterOptions}
                className="custom-header"
              />
            </div>
          </div>
          {openConfigureModal ? (
            <ReportSideModal
              filterReportData={filterReportData}
              filtersData={filters}
              closeConfigureModal={() => setOpenConfigureModal(false)}
              filter={filter}
              handleClose={() => closeConfigureModal()}
            />
          ) : (
            <></>
          )}
          {openSavedHistoryModal ? (
            <ReportHistoryModal
              closeConfigureModal={() => setOpenSavedHistoryModal(false)}
              fetchData={fetchData}
              reportEdit={reportEdit}
              filters={reportFilters}
              loadingFilterRecords={loadingFilterRecords}
              clearFilters={clearFilters}
            />
          ) : (
            <></>
          )}
        </div>
        <SaveReportModal
          modalOpen={modalOpen}
          handleClose={handleClose}
          handleOpen={handleOpen}
          filters={filters}
          setFilterData={(filter) => setFilter(filter)}
        />
      </ApplicationLayout>
    </React.Fragment>
  )
}

const mapStateToProps = (state) => {
  const { reports } = state
  return {
    currentUser: state.application.userSessionData || (state.users && state.users.currentUser),
    transactionDataSource: reports.transactionReportData.orders,
    loadingTransactionRecords: reports.loadingTransactionRecords,
    totalPages: reports.transactionReportData.total_pages,
    totalEntries: reports.transactionReportData.total_reocrds,
    reportSummaryData: reports.reportSummaryData,
    reportFilters: reports.filters,
    loadingFilterRecords: reports.loadingFilterRecords
  }
}

export default connect(mapStateToProps, null)(FMCReporting)
